import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'environments/environment';

export interface TotpEnrolResponse {
  success: { provisioning_uri: string };
}

export interface TotpConfirmResponse {
  success: { recovery_codes: string[] };
}

export interface TotpDisablePayload {
  code?: string;
  recovery_code?: string;
}

@Injectable({
  providedIn: 'root',
})
export class TotpService {
  // `enrol` mutates server-side state (rotates the pending secret) on every
  // call. Holding the last response here — root-provided, so it survives
  // navigating away and back within the session — lets the enrolment screen
  // redisplay the SAME QR instead of silently minting a new one and
  // invalidating whatever the user already scanned.
  private pending: TotpEnrolResponse | null = null;

  // What this session has actually observed about enrolment, which outranks the
  // login response's `two_factor_method`. That hint is captured at login and
  // goes stale the moment the user enrols or disables here -- and a stale
  // `'totp'` used to land a just-disabled user on the "enabled" screen with no
  // way back to setup. null means "nothing observed, fall back to the hint".
  private enrolledInSession: boolean | null = null;

  constructor(private http: HttpClient) {}

  getEnrolledInSession(): boolean | null {
    return this.enrolledInSession;
  }

  enrol(): Observable<TotpEnrolResponse> {
    return this.http
      .post<TotpEnrolResponse>(`${environment.apiURL}totp/enrol`, {})
      .pipe(tap((res) => (this.pending = res)));
  }

  getPendingEnrolment(): TotpEnrolResponse | null {
    return this.pending;
  }

  clearPendingEnrolment() {
    this.pending = null;
  }

  confirm(code: string): Observable<TotpConfirmResponse> {
    return this.http
      .post<TotpConfirmResponse>(`${environment.apiURL}totp/confirm`, { code })
      .pipe(
        tap(() => {
          this.pending = null;
          this.enrolledInSession = true;
        })
      );
  }

  disable(payload: TotpDisablePayload): Observable<any> {
    return this.http
      .post(`${environment.apiURL}totp/disable`, payload)
      .pipe(tap(() => (this.enrolledInSession = false)));
  }

  // Called when `enrol` returns 400 -- the server's own statement that this
  // account is already enrolled, which is stronger than either hint.
  markEnrolled() {
    this.enrolledInSession = true;
  }

  // Called when a login was completed with a recovery code -- the server resets
  // enrolment in that flow, so the login response's `'totp'` hint is stale
  // before this session ever reaches the security screen.
  markUnenrolled() {
    this.enrolledInSession = false;
  }

  // 🔴 Both fields above belong to one signed-in user, but this service is
  // root-provided and `JwtAuthService.signout()` only routes -- it never
  // reloads the page -- so without this they outlive the user that set them.
  // Measured on systemTest 2026-09-12: after one account finished enrolment,
  // the next account signing in on the same tab was sent to the forced
  // enrolment screen and shown "already enabled / move to a new phone", which
  // it can never clear because it has no enrolment to move. `pending` is worse
  // than stale -- it would hand the new user the previous user's provisioning
  // URI, secret included.
  clearSessionState() {
    this.pending = null;
    this.enrolledInSession = null;
  }
}
